import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import React from "react";

type DesktopNavItemProps = {
  item: {
    name: string;
    href: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | null;
  };
  onClick?: () => void;
  onClose?: () => void;
};

type NavbarItemContentProps = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | null;
  children: React.ReactNode;
};

export function NavbarItemContent({
  icon: Icon,
  children,
}: NavbarItemContentProps) {
  return (
    <>
      <div className="settings-icon-wrapper">
        {Icon ? (
          <Icon aria-hidden="true" className="settings-icon" />
        ) : (
          <Image
            aria-hidden="true"
            height={16}
            width={16}
            alt="paw"
            src="/image/paw/paw-dark.svg"
            className="settings-icon"
          />
        )}
      </div>

      <div className="min-w-0 flex-auto">{children}</div>

      <ChevronRightIcon aria-hidden="true" className="settings-chevron-shift" />
    </>
  );
}

export const DesktopNavItem: React.FC<DesktopNavItemProps> = ({
  item,
  onClick,
  onClose,
}) => {
  const { setMobileMenuOpen } = useAppContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      labels: {
        "Vytvořit nového": "Vytvořit nového",
        Zobrazit: "Zobrazit",
        "Spravovat existujícího": "Spravovat existujícího",
        "Přidat transakci": "Přidat transakci",
        "Upravit nemovitost": "Upravit nemovitost",
        "Přidat nemovitost": "Přidat nemovitost",
        "Přepnout uživatele": "Přepnout uživatele",
        Logy: "Logy",
        "Změnit členství": "Změnit členství",
        "Změnit platební údaje": "Změnit platební údaje",
        "Změnit fakturační údaje": "Změnit fakturační údaje",
        "Změnit heslo": "Změnit heslo",
        "Smazat účet": "Smazat účet",
        "Nahlásit chybu": "Nahlásit chybu",
        "Požádat o novou funkci": "Požádat o novou funkci",
      },
    },
    en: {
      labels: {
        "Vytvořit nového": "Create new",
        Zobrazit: "View",
        "Spravovat existujícího": "Manage existing",
        "Přidat transakci": "Add transaction",
        "Upravit nemovitost": "Edit property",
        "Přidat nemovitost": "Add property",
        "Přepnout uživatele": "Switch user",
        Logy: "Logs",
        "Změnit členství": "Change membership",
        "Změnit platební údaje": "Change payment details",
        "Změnit fakturační údaje": "Change billing details",
        "Změnit heslo": "Change password",
        "Smazat účet": "Delete account",
        "Nahlásit chybu": "Report a bug",
        "Požádat o novou funkci": "Request a new feature",
      },
    },
  }[language];
  const { setIsOpen, setSelectedItem } = useMenuItemsContext();
  const itemLabel =
    copy.labels[item.name as keyof typeof copy.labels] ?? item.name;

  const handleClick = onClick
    ? onClick
    : () => {
        if (onClose) onClose();
        setSelectedItem(item);
        setIsOpen(true);
        setMobileMenuOpen(false);
      };

  return (
    <div
      key={item.name}
      onClick={handleClick}
      className="group settings-row cursor-pointer"
    >
      <NavbarItemContent icon={item.icon}>
        <Link href={item.href} className="settings-label truncate">
          {itemLabel}
          <span className="absolute inset-0" />
        </Link>
      </NavbarItemContent>
    </div>
  );
};
